// Libs
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
// Utils
import GENERAL from 'utils/constants/general';
import { getUIValueProp } from 'components/DataFieldManager/OnlyReadField/OnlyReadField';

// Actions
import { sendToast } from 'screens/Dashboard/actions';
import { updateDatasourceItem, removeDatasourceItem } from 'core/actions';
// Hooks
import { useOrderApi, useServiceApi } from 'hooks';
import { GlobalUtils } from 'utils';
import { shouldRemoveOrderFromDatasource } from './useOrderServiceAssignment.utils';

const { UNSUCCESS } = GENERAL.ENV.STATUS;

const useOrderServiceAssignment = () => {
	const dispatch = useDispatch();
	const { getServices } = useServiceApi();
	const { setServiceAssignment } = useOrderApi();
	const isMountedRef = useRef(true);
	const getRequestIdRef = useRef(0);

	const [state, setState] = useState({
		isLoading: false,
		isSetting: false,
		data: [],
		status: undefined,
		message: undefined,
		selectedServiceId: undefined,
	});

	useEffect(
		() => () => {
			isMountedRef.current = false;
			getRequestIdRef.current += 1;
		},
		[],
	);

	const onGetServiceAssignment = async () => {
		if (!isMountedRef.current) return;

		const requestId = ++getRequestIdRef.current;
		setState(prev => ({
			...prev,
			isLoading: true,
			data: [],
			status: undefined,
			message: undefined,
		}));

		try {
			const response = await getServices();
			if (!isMountedRef.current || requestId !== getRequestIdRef.current)
				return;

			setState(prev => ({
				...prev,
				data: GlobalUtils.checkArray(response),
			}));
		} catch (err) {
			if (!isMountedRef.current || requestId !== getRequestIdRef.current)
				return;

			setState(prev => ({
				...prev,
				data: [],
				status: UNSUCCESS,
				message: err.message || '',
			}));
			dispatch(sendToast({ message: err.message, type: err.type }));
		} finally {
			if (isMountedRef.current && requestId === getRequestIdRef.current)
				setState(prev => ({ ...prev, isLoading: false }));
		}
	};

	const onRetry = () => onGetServiceAssignment();

	const onSetServiceAssignment = async ({
		field,
		rowData,
		selected,
		option,
		cleanElementView,
	}) => {
		setState(prev => ({ ...prev, isSetting: true }));
		const getUpdateRecords = (data, { field, option }) => {
			const record = {
				fields: {
					[field.id]: option.id,
					[getUIValueProp(field) || field.name]: option.name,
				},
			};

			return [{ id: data.id, ...record }];
		};

		setServiceAssignment(rowData.id, option.id)
			.then(response => {
				const processedOrder = response.data;
				const currentOrderTargetId = rowData.orderTargetId;
				const newOrderTargetId = processedOrder.orderTargetId;
				const isFailed = response.status === UNSUCCESS;

				setState(prev => ({ ...prev, isSetting: false }));

				if (isFailed) {
					dispatch(sendToast({ message: response.message, type: 'error' }));
					return;
				}

				const updateRecords = !!selected
					? getUpdateRecords(processedOrder, { field, option })
					: getUpdateRecords(rowData, { field, option });

				dispatch(updateDatasourceItem({ updateRecords }));
				setState(prev => ({ ...prev, selectedServiceId: option.id }));

				// The datasource is removed only from an ElementView. Feed rows do not
				// receive this callback, so removing them there would hide a valid row.
				if (
					shouldRemoveOrderFromDatasource({
						currentOrderTargetId,
						newOrderTargetId,
						cleanElementView,
					})
				) {
					dispatch(removeDatasourceItem(rowData.id));
					cleanElementView();
				}
			})
			.catch(err => {
				setState(prev => ({
					...prev,
					isSetting: false,
					status: UNSUCCESS,
					message: err.message || '',
				}));
				dispatch(sendToast({ message: err.message, type: err.type }));
			});
	};
	return {
		state,
		onRetry,
		onGetServiceAssignment,
		onSetServiceAssignment,
	};
};

export default useOrderServiceAssignment;
